/**
 * Nettoyage des évènements Sentry du front standalone (PUR, testé par `node:test`).
 *
 * Le standalone porte trois routes dont l'URL contient un secret :
 * - `/sso?token=…` : jeton SSO entrant de la Trilogie (SD-4544, cf. ADR-044), vivant 60 s ;
 * - `/set-password/:token` et `/forgot-password/:token` : liens magiques.
 *
 * `Sentry.init` s'exécute avant que `SsoView` ne retire le jeton de la barre d'adresse :
 * l'URL complète peut donc se retrouver dans `event.request.url`, le nom de la transaction
 * de chargement de page, les fils d'Ariane de navigation et de requêtes, et les attributs
 * des spans. Le nettoyage parcourt donc TOUT l'évènement et réécrit chaque chaîne, au lieu
 * d'énumérer des champs (un champ oublié serait une fuite silencieuse).
 */

export const FILTERED = '[Filtered]'

/**
 * Clés dont la valeur n'est jamais transmise : en-têtes sensibles, et tout attribut dont le
 * nom se termine par `token` — l'intégration Vue Router de Sentry recopie les paramètres de
 * la route en attributs de la transaction (`query.token`, `params.token`,
 * `url.path.parameter.token`), valeurs nues qu'aucun motif d'URL ne reconnaît.
 */
const SENSITIVE_KEY_RE = /authorization|cookie|x-api-key|password|secret|token$/i

/** Toute la query string d'une URL `/sso` (le jeton peut changer de nom sans prévenir). */
const SSO_QUERY_RE = /(\/sso\/?)\?[^#\s"'<>]*/gi

/** Paramètre dont le nom se termine par `token` (`token`, `access_token`, `refresh_token`…). */
const TOKEN_PARAM_RE = /([?&#;][\w.-]*token=)[^&#\s"'<>]*/gi

/**
 * Segment de chemin qui suit `/set-password/` ou `/forgot-password/` (route du front comme
 * appel d'API `/v1/auth/set-password/<jeton>`). Un motif de route (`:token`) est conservé :
 * c'est le nom paramétré de la transaction, utile au regroupement et sans secret.
 */
const TOKEN_PATH_RE = /(\/(?:set-password|forgot-password)\/)(?!:)[^/?#\s"'<>]+/gi

/** Profondeur maximale parcourue (un évènement Sentry est un JSON peu profond). */
const MAX_DEPTH = 24

/** Retire de `text` tout jeton porté par une URL. Idempotent. */
export function scrubSensitiveText(text: string): string {
  return text
    .replace(SSO_QUERY_RE, '$1')
    .replace(TOKEN_PARAM_RE, `$1${FILTERED}`)
    .replace(TOKEN_PATH_RE, `$1${FILTERED}`)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]'
}

function scrubValue(value: unknown, depth: number, copies: Map<object, unknown>): unknown {
  if (typeof value === 'string') return scrubSensitiveText(value)
  if (value === null || typeof value !== 'object') return value
  // Au-delà de la profondeur parcourue, rien n'est transmis plutôt qu'un contenu non nettoyé.
  if (depth >= MAX_DEPTH) return FILTERED
  // Référence déjà rencontrée (cycle ou objet partagé) : sa copie nettoyée, jamais l'original.
  if (copies.has(value)) return copies.get(value)
  if (Array.isArray(value)) {
    const copy: unknown[] = []
    copies.set(value, copy)
    for (const item of value) copy.push(scrubValue(item, depth + 1, copies))
    return copy
  }
  if (!isPlainObject(value)) return value
  const copy: Record<string, unknown> = {}
  copies.set(value, copy)
  for (const [key, item] of Object.entries(value)) {
    copy[key] = SENSITIVE_KEY_RE.test(key) && (typeof item === 'string' || Array.isArray(item))
      ? FILTERED
      : scrubValue(item, depth + 1, copies)
  }
  return copy
}

/**
 * Copie de `value` où chaque chaîne est nettoyée de ses jetons et la valeur de chaque clé
 * sensible (en-têtes `authorization`/`cookie`/`x-api-key`, `*password*`, `*secret*`,
 * `*token`) remplacée par `[Filtered]`.
 * Les objets non « plats » (classes, Date, Error…) sont laissés tels quels.
 */
export function scrubDeep<T>(value: T): T {
  return scrubValue(value, 0, new Map<object, unknown>()) as T
}
